import { useRef, type KeyboardEvent } from "react";

/**
 * In-page tabs: a few views of one thing, under one page header.
 *
 * Real tab semantics, because the panel below swaps entirely: `tablist`, `tab`, `aria-selected`,
 * and the arrow keys move between them with focus following. The selected tab is the only one in
 * the Tab order, which is what the pattern asks for and what keeps a page with three tab strips
 * from costing twelve Tab presses to cross.
 *
 * Panels are the caller's. `panelId(tab.id)` names them, so each panel's `aria-labelledby` can
 * point back at its tab.
 */
/**
 * `todo` marks a count as work still to do (people to sort, parents to link): the count is gold,
 * like the other "to do" chips, and its accessible name says "to do".
 */
/** `hint`: a second, smaller line under the label ("29 without a name"), for steps of one task. */
export type TabOption = { id: string; label: string; count?: number; todo?: boolean; hint?: string };

export function Tabs({
  tabs,
  selectedId,
  onSelect,
  label,
}: {
  tabs: TabOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** The list's accessible name, "Inbox views". */
  label: string;
}) {
  const refs = useRef<Map<string, HTMLButtonElement>>(new Map());

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step =
      event.key === "ArrowRight" ? 1
      : event.key === "ArrowLeft" ? -1
      : event.key === "Home" ? -index
      : event.key === "End" ? tabs.length - 1 - index
      : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    if (!next) return;
    onSelect(next.id);
    refs.current.get(next.id)?.focus();
  }

  return (
    // Scrolls sideways when the tabs do not fit (four tabs with counts at 390px), so the page
    // itself never does. The rule under the tabs is an inset shadow rather than a border, because
    // the scroll frame clips at its border and would cut the selected tab's underline in half.
    <div
      role="tablist"
      aria-label={label}
      className="scroll-x mb-4 flex gap-1 overflow-x-auto shadow-[inset_0_-1px_0_var(--color-line)]"
    >
      {tabs.map((tab, index) => {
        const selected = tab.id === selectedId;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) refs.current.set(tab.id, node);
              else refs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            id={tabId(tab.id)}
            aria-selected={selected}
            aria-controls={panelId(tab.id)}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={[
              // `relative`: the hidden "to do" words inside are placed in the tab, not past the scroll frame.
              "relative inline-flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-[14px] font-semibold whitespace-nowrap transition-colors duration-150",
              selected
                ? "border-accent text-accent-strong"
                : "border-transparent text-ink-2 hover:text-ink",
            ].join(" ")}
          >
            {tab.hint ? (
              <span className="flex flex-col items-start py-1.5 text-left leading-tight">
                <span>{tab.label}</span>{" "}
                <span className="text-[12px] font-normal text-ink-2">{tab.hint}</span>
              </span>
            ) : (
              tab.label
            )}
            {tab.count !== undefined ? (
              <span
                className={[
                  "rounded-pill px-1.5 text-[11px] tabular-nums",
                  tab.todo && tab.count > 0
                    ? "bg-late-surface font-bold text-late-ink"
                    : selected
                      ? "bg-accent-surface text-accent-strong"
                      : "bg-sunken text-ink-2",
                ].join(" ")}
              >
                {tab.count}
                {tab.todo && tab.count > 0 ? <span className="sr-only"> to do</span> : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function tabId(id: string): string {
  return `tab-${id}`;
}

export function panelId(id: string): string {
  return `tabpanel-${id}`;
}
